import { api, ApiError } from "./client";

// Wire mirror of the frozen Buyer Listing Research contract (a) —
// `Core/DTOs/Buyers/BuyerResearchDtos.cs`. The C# side is positional records
// that extend only with trailing defaulted members, so a member added there
// must be appended here too or it never reaches a component. Enums are
// string-stored, so the unions below are the literal values on the wire.

export type BuyerResearchRunStatus =
  | "Queued"
  | "Running"
  | "Completed"
  | "CompletedPartial"
  | "Failed"
  | "Cancelled"
  /**
   * A COMPLETED run's Pull more was ACCEPTED and waits for the worker.
   *
   * <p>The pull POST returns immediately now: the buy and the rank run on the
   * background worker, so the run sits here until a worker picks the pull up,
   * then moves to `Pulling`, then back to a terminal status. Not terminal —
   * the client keeps polling and draws progress.</p>
   */
  | "PullQueued"
  /**
   * A COMPLETED run is buying its next batch (RC9).
   *
   * 🔴 It is NOT terminal, and it is not `Running` either. A first run has
   * nothing on screen and a pull has a list that must stay exactly where it is,
   * so the two draw different spinners; and the stuck-run sweeper fails a
   * long-lived `Running` row, which on a run whose candidates somebody is reading
   * would destroy the thing they are reading.
   */
  | "Pulling"
  /**
   * A COMPLETED run is applying an Ask Phoenix refinement.
   *
   * 🔴 Not terminal, not `Running`, and not `Pulling` either. Not `Running` for
   * the sweeper's sake, exactly as `Pulling` is not; not `Pulling` because a
   * refinement may buy nothing at all, and a surface saying a batch is on its
   * way when none is would be a promise nobody made.
   */
  | "Refining";

/** `succeeded | empty | failed | skipped` — one registry source's outcome. */
export type BuyerResearchSourceOutcome = "succeeded" | "empty" | "failed" | "skipped";

/** Every buyer-fit / program-fit flag takes exactly one of these. */
export type BuyerResearchFlagValue = "yes" | "no" | "unknown";

export type BuyerResearchSellerFinancing = "required" | "preferred" | "any";

/**
 * How a candidate's broker contact is reachable (D10 / D14).
 *
 * `on_request` is Clef's own: the source HOLDS the broker's details and releases
 * them on a per-listing call, so the card offers GET BROKER CONTACT rather than
 * sending the specialist elsewhere. A fetch flips it to `shown`, or to `source`
 * when Clef turns out to hold nothing.
 */
export type BuyerResearchContactVia = "shown" | "source" | "on_request";

/**
 * Which published figure `cashFlow` came from. `ebitda` is the last resort and
 * is why a program-fit multiple can read `unknown` on a candidate that shows a
 * cash-flow number.
 */
export type BuyerResearchCashFlowBasis = "sde" | "cashFlow" | "ebitda";

export interface BuyerResearchGeographyDto {
  state: string | null;
  metro: string | null;
}

/**
 * The search brief (D2) with a provenance label per field. `provenance` is
 * keyed by field name for scalars (`askingPriceMax`) and by `industries[0]` /
 * `geographies[1]` for list items; values are the `BuyerResearchProvenance`
 * labels (`fromField:MaxPurchasePrice`, `interview:GEO1`, `biography`,
 * `prequalification`, `proposedByAi`, `specialist`). `statedPriceBand` is
 * context only (RL10) and never feeds the search.
 */
export interface BuyerResearchBriefDto {
  industries: string[];
  geographies: BuyerResearchGeographyDto[];
  willRelocate: boolean | null;
  askingPriceMin: number | null;
  askingPriceMax: number | null;
  cashFlowMin: number | null;
  revenueMin: number | null;
  sellerFinancing: BuyerResearchSellerFinancing;
  exclusions: string | null;
  notes: string | null;
  provenance: Record<string, string>;
  statedPriceBand: string | null;

  /**
   * The NAICS codes each industry term resolved to (D6), editable before the run.
   *
   * 🔴 A term with NO ENTRY and an entry with an empty `codes` array mean
   * OPPOSITE things (RA6). No entry means nobody has resolved the term and the
   * server will on the next brief build; an empty entry is an ANSWER, and
   * sending one back re-asserts it rather than asking again. So a specialist who
   * prunes every suggestion must leave the entry behind with `codes: []` — drop
   * the whole entry and their pruning is undone on the next run.
   */
  industryCodes: BuyerResearchIndustryCodesDto[] | null;

  /**
   * A specialist settled this brief in CONVERSATION, counting each version
   * against Clef's free endpoint before running it (RE3) — so the run it
   * starts must buy this query as they left it rather than narrowing it again.
   *
   * 🔴 **THE CLIENT IS THE ONLY THING THAT CAN SET THIS.** RE2 keeps the
   * transcript and the working brief client-held, so nothing server-side can
   * confirm a conversation happened. `StartRunAsync` persists the brief into
   * `BriefJson`, `ReadBrief` reads it back, and
   * `SourceBudget.QuerySettledInConversation` is what `ClefListingSource`
   * consults to skip the adaptive probe. Omit it and the probe narrows the
   * query the specialist just settled, underneath them.
   *
   * 🔴 **It never reaches a filter.** `ToSearchBrief` drops it, so no
   * fingerprint moves and a conversational run compares EQUAL to an identical
   * non-conversational one under the re-run guard — which is right: that
   * guard asks whether this buyer has already bought this market, not how the
   * query was authored.
   *
   * OPTIONAL here and non-nullable (`= false`) on the server, which is the
   * `LoanSummaryDto` convention: a brief literal written before this member
   * existed still typechecks, and an omitted member deserialises to false.
   */
  settledInConversation?: boolean;
}

/**
 * One industry term and the codes it resolved to. Three states, not two:
 * no entry (unasked), an entry with codes (resolved), and an entry with none —
 * which is either Clef matching nothing (`unresolved` true, with a reason) or a
 * specialist having pruned them all (`unresolved` false).
 */
export interface BuyerResearchIndustryCodesDto {
  term: string;
  codes: BuyerResearchIndustryCodeDto[];
  unresolved: boolean;
  unresolvedReason: string | null;
}

/** One resolved NAICS code. `matchedVia` is Clef's account of WHY it matched,
 * shown so a specialist can prune a wrong one knowingly. */
export interface BuyerResearchIndustryCodeDto {
  code: string;
  title: string;
  matchedVia: string | null;
}

export interface StartBuyerResearchRunRequest {
  brief: BuyerResearchBriefDto;
}

/** What a specialist typed into Ask Phoenix. */
export interface BuyerResearchRefineRequest {
  text: string;
}

export interface BuyerResearchSourceLogEntryDto {
  sourceKey: string;
  displayName: string;
  accessMode: string;
  outcome: BuyerResearchSourceOutcome;
  error: string | null;
  candidateCount: number;
  searchedUrls: string[];
  fetchedUrls: string[];
  estimatedCostUsd: number;
  durationMs: number;

  /**
   * What a METERED source DELIVERED and was therefore charged for — not what its
   * mapper kept, and not what survived de-duplication.
   *
   * 🔴 RC8: this is the number the specialist is owed. A page of 20 that yields
   * 16 cards still spent 20 records of the day's allowance. Never show 16 and
   * call it 16. 0 on every unmetered source, which is true of them.
   */
  recordsUsed: number;

  /** Candidates this attempt bought that the run ALREADY held under the same
   * fingerprint, so they were dropped rather than added — and paid for all the
   * same, because the feed is offset-paged over a list that moves. */
  duplicatesDropped: number;

  /** Candidates this attempt added that the buyer has already dismissed. They
   * are persisted and flagged rather than dropped, so they are not duplicates —
   * a separate number for a separate fact. */
  alreadyDismissed: number;

  /** 1 is the run's own search; 2 and up are pulls. The newest entry above 1 is
   * the last pull, which is how the control finds the numbers for its own line. */
  batchNumber: number;

  /** Quick search only: plain sentences about how the source read the typed
   * search (e.g. a city Clef could not filter on). Absent on every ordinary run. */
  queryNotes?: string[] | null;

  // ---- the adaptive-search probe (Area C). Null on every source that cannot
  // narrow its own query, which is all of them but Clef — and that null reads
  // as "it searched what its brief derives", which is true of them.

  /** What the probe did, from `BuyerResearchProbeOutcomes`. */
  probeOutcome: BuyerResearchProbeOutcome | null;

  /** How many FREE count variants it tried. Costs nothing and is reported
   * anyway: a specialist asking "why these listings?" is owed the size of the
   * search that produced them. */
  probeVariantsTried: number;

  /** The population the brief matched before the probe narrowed it, and after.
   * Equal when the probe changed nothing.
   *
   * Nullable because **0 is a real answer** — a filter that matches nothing is
   * a fact worth showing, and it must not read the same as a source that was
   * never probed. */
  matchesBeforeProbe: number | null;

  /** @see matchesBeforeProbe */
  matchesAfterProbe: number | null;

  /**
   * The share of listings that PUBLISH a multiple whose multiple is at or under
   * the program ceiling.
   *
   * 🔴 A share of the PUBLISHERS, not of the population. Reading it as "x% of
   * this market fits the program" overstates it by exactly `1 - publicationRate`,
   * which is why the two are always rendered together.
   */
  fitDensity: number | null;

  /** The share of the converged population publishing a multiple at all — the
   * denominator behind `fitDensity`, and on its own the answer to "is this
   * market thin, or just quiet?". */
  publicationRate: number | null;

  /** The transcript, oldest attempt first. Null on a source that does not
   * probe; empty on one that probed and tried nothing. */
  probeSteps: BuyerResearchProbeStepDto[] | null;

  /** Why a source that CAN narrow chose not to, in words. Null whenever the
   * probe actually ran. Without it `skipped` has three causes and one
   * appearance, and one of them is a specialist's own instruction. */
  probeSkippedReason: string | null;
  /**
   * The Ask Phoenix refinement that bought this batch, or null on every batch
   * the run bought for itself. The HISTORY half of the refinement split — the
   * run carries one pointer to what is applied now; this says which batch each
   * refinement ever bought.
   */
  refinementId: string | null;

  /**
   * Ruling 7 — did Clef's financial bounds let listings that publish no figure
   * through, on this source's own query?
   *
   * 🔴 **NULL IS "NOBODY MEASURED", NEVER FALSE.** The probe did not run, it
   * spent its budget narrowing instead, the vendor refused the count parameter,
   * or Clef is off. Rendering null as "the ceiling dropped them" turns an absent
   * measurement into a confirmation of the very inference it exists to test
   * — which is D5's premise for banning every financial bound.
   *
   * A by-product of three FREE counts the probe already spends, so it costs
   * nothing to have.
   */
  nullsPassAnUpperBound?: boolean | null;

  /**
   * Rows this attempt BOUGHT that were never scored, and are therefore never
   * shown (R1/R15). 0 — or absent, on a log written before the field existed —
   * means every row was scored.
   *
   * <p>🔴 The records were still charged: the vendor bills on delivery, so a
   * row that could not be scored cost the same as one that could.</p>
   */
  unscored?: number;

  /**
   * Why {@link unscored} rows were not scored. Four codes have sentences of
   * their own — `"chunk failed"`, `"spend cap reached"`, `"time limit"` (the
   * run reached its time limit before their chunk started) and `"run stopped"`
   * (the daily AI ceiling or a cancel did). Any other value renders the generic
   * count alone.
   */
  unscoredReason?: string | null;
}

/**
 * The four `BuyerResearchUnscoredReasons` codes the surface has a sentence of
 * its own for — `unscoredReason` on a source-log entry.
 *
 * 🔴 The FIELD stays `string | null` and this union does not narrow it: the
 * server's vocabulary is a set of constants, and a code this client has not
 * learned yet must still render (as the generic count) rather than fail to
 * parse. The union exists so the copy map keyed on it is EXHAUSTIVE — a member
 * added here without a sentence is a type error, not a silent fall-through.
 */
export type BuyerResearchUnscoredReason =
  | "chunk failed"
  | "spend cap reached"
  | "time limit"
  | "run stopped";

/** What the probe did overall. */
export type BuyerResearchProbeOutcome =
  | "converged"
  | "already-in-band"
  | "floor-reached"
  | "budget-spent"
  | "skipped";

/**
 * One thing the probe tried, and what the FREE count said about it.
 *
 * Every step cost nothing: a specialist reading the transcript is reading the
 * reasoning, not an invoice.
 */
export interface BuyerResearchProbeStepDto {
  /** Which lever moved, from `BuyerResearchProbeLevers`. */
  lever: string;
  /** The change in words ("industry 238 → 238220"), not a serialised filter. */
  change: string;
  /** What the free count returned. **Null when the probe refused the variant
   * WITHOUT counting it** — a proposal that failed the prefix rule never
   * reached the vendor, so there is no number for that row. */
  matches: number | null;
  accepted: boolean;
  /** Why not, when it was not. Null when accepted. */
  rejectedBecause: string | null;
}

/** A quick-search row's verdict against the typed search (M3). Null on an ordinary run. */
export type BuyerResearchMatchStatus = "Match" | "Unconfirmed" | "NoMatch";

/** `Standard` is every ordinary run; `Quick` is a typed Quick search. */
export type BuyerResearchRunKind = "Standard" | "Quick";

/** One criterion read from the typed sentence. `kind` is one of `BuyerResearchQuickCriterionKinds`. */
export interface BuyerResearchQuickCriterionDto {
  key: string;
  kind: string;
  label: string;
}

/** How the server read a quick search. Composed by code, never by the model. */
export interface BuyerResearchQuickSearchInterpretationDto {
  text: string;
  keywords: string[];
  states: string[];
  cities: string[];
  askingPriceMin: number | null;
  askingPriceMax: number | null;
  cashFlowMin: number | null;
  revenueMin: number | null;
  criteria: BuyerResearchQuickCriterionDto[];
  readLine: string;
}

/** The run summary's quick-search block. */
export interface BuyerResearchQuickSearchDto {
  text: string;
  readLine: string;
  notes: string[];
  interpretation: BuyerResearchQuickSearchInterpretationDto;
}

/** One criterion's verdict on one quick-search candidate. `value` is yes / no / unknown. */
export interface BuyerResearchCandidateCriterionDto {
  key: string;
  label: string;
  value: BuyerResearchFlagValue;
  reason: string | null;
}

export interface StartBuyerResearchQuickSearchRequest {
  text: string;
}

/** A quick run's counts line: Clef found N · scored · match · couldn't confirm · didn't match. */
export interface BuyerResearchQuickCountsDto {
  found: number | null;
  scored: number;
  matches: number;
  unconfirmed: number;
  noMatch: number;
  dismissed: number;
}

export interface BuyerResearchCandidateDto {
  id: string;
  fingerprint: string;
  shown: boolean;
  sourceKey: string;
  sourceDisplayName: string;
  sourceUrl: string;
  alsoSeenAt: string[];
  listingReference: string | null;
  title: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  industry: string | null;
  askingPrice: number | null;
  cashFlow: number | null;
  revenue: number | null;
  ebitda: number | null;
  sellerFinancingMentioned: boolean;
  sellerFinancingQuote: string | null;
  /** `DateOnly?` on the wire — `yyyy-MM-dd`, a wall-calendar value. */
  listedDate: string | null;
  snippet: string;
  summary: string;
  brokerName: string | null;
  brokerFirm: string | null;
  brokerPhone: string | null;
  brokerEmail: string | null;
  contactVia: BuyerResearchContactVia;
  linkOnly: boolean;
  retrievedAtUtc: string;
  fitScore: number | null;
  reasons: string[];
  buyerFit: Record<string, BuyerResearchFlagValue>;
  programFit: Record<string, BuyerResearchFlagValue>;
  recommended: boolean;
  recommendationNote: string | null;
  /** Quick search only. */
  matchStatus?: BuyerResearchMatchStatus | null;
  /** Quick search only: the verdict on each criterion read from the typed sentence. */
  criteria?: BuyerResearchCandidateCriterionDto[] | null;
  existingListingId: string | null;
  existingListingDisplayName: string | null;
  possibleDuplicateOfListingId: string | null;
  possibleDuplicateDisplayName: string | null;
  attachedListingId: string | null;
  attachedAssociationId: string | null;
  attachedAtUtc: string | null;
  isDismissed: boolean;
  /**
   * Attached to this buyer NOW — the same live rule as the row's `isAttached`.
   * Set on the opened candidate and the broker-contact response; false on the
   * full run read, which no card renders.
   */
  isAttached: boolean;

  // ---- Clef-sourced (D14). Null on every other source AND on the rows that
  // predate them, which is exactly what the card's null gates rely on.

  /** Clef's listing id. 🔴 The card offers GET BROKER CONTACT only when this is
   * non-null, so a historical row with `sourceKey: "clef"` and no id degrades
   * quietly instead of offering a fetch that cannot address anything. */
  clefListingId: string | null;

  /** The originating marketplace — the card reads "{marketplace} · via Clef". */
  marketplaceName: string | null;

  /** Clef's confidence in its own NAICS classification, 0..1. The
   * classification is derived rather than published, so a low value is labelled. */
  industryConfidence: number | null;

  cashFlowBasis: BuyerResearchCashFlowBasis | null;

  /** When the broker contact was last fetched, so the card shows the action as
   * already spent rather than inviting a second charged call. */
  contactFetchedAtUtc: string | null;

  /**
   * Which batch bought this candidate: 1 is the run's own search, 2 and up are
   * pulls.
   *
   * 🔴 It is the THIRD key of Best fit on the server, after fit and recommended
   * (R3). It used to be the first ("append below, never reorder"); R3 reversed
   * that, so a pull's strong row can sort above cards already read. What survives
   * is that no card leaves the list. Defaults to 1 for every candidate written
   * before paging existed.
   */
  batchNumber: number;

  // ---- Layer B carriers (WS5) --------------------------------------------
  //
  // 🔴 EVERY ONE IS NULL ON EVERY ROW WRITTEN BEFORE THE COLUMNS EXISTED, and
  // on every non-Clef source. There is no backfill and no default, so a surface
  // that renders one unconditionally prints an empty label on the whole
  // historical population — which is worse than not rendering it at all.
  // Gate each one.
  //
  // They exist because ruling 1 routes a request the vendor cannot be asked to
  // Layer B — a free, reversible filter over records already bought — and
  // Layer B can only answer what Phoenix persists. The names mirror the
  // entity's exactly and carry no `Clef` prefix BY CONTRACT:
  // `BuildHonestRouteFields` reflects over `BuyerResearchCandidate`'s property
  // names to decide whether a route may name a field, so prefixing either tier
  // drops five of the ten route fields in silence.

  /** Clef's own tags, as the JSON array the vendor sent —
   * `["seller_financing", "sba_eligible"]`. A string rather than a parsed list
   * because it is stored verbatim; null when the vendor sent none. */
  tagsJson?: string | null;

  /** The staff count as the vendor published it — a STRING, because Appendix A
   * types it one and the value may be a range ("10-20"). Null means UNKNOWN and
   * never "few". Empty on every row today: `ClefApiClient.ReadInt` still
   * requires a JSON number for it. */
  employees?: string | null;

  /** Asset sale or stock sale, which is materially different for lending. */
  dealType?: string | null;

  /** When the vendor last saw the listing CHANGE — "has the price moved?".
   * Distinct from `listedDate` (first seen, narrowed to a day) and
   * `retrievedAtUtc` (when Phoenix bought it). */
  lastChangeAt?: string | null;

  /** `active`, or `delisted` once withdrawn at source. Reads `active` wherever
   * populated, because the mapper drops a delisted listing; it earns its place
   * on the refresh path. */
  status?: string | null;

  /** The currency the money fields are stated in. `USD` wherever populated,
   * because non-USD rows are dropped at the mapper — storing it makes that
   * filtering visible rather than an unexplained gap. */
  currency?: string | null;

  /** The vendor's finer classification on its own, rather than fused with the
   * NAICS code into `industry` — which is right for a label and wrong for a
   * filter. */
  subIndustry?: string | null;

  /** The vendor's NAICS code on its own.
   *
   * 🔴 It is PROVENANCE for the industry the row already states in words, not a
   * column a specialist scans — `industry` is a label the mapper composed as
   * `"Specialty Trade Contractors (238220)"`, and the code is carried apart from
   * it so a card can show the words without the parenthetical. It is also what
   * the server compares against the codes the run executed; reading it as the
   * industry itself puts a number where a reader needs a noun. */
  naicsCode?: string | null;
}

/** Sort keys the list endpoint accepts. `fit` is Best fit (R3), one fixed order. */
export type BuyerResearchCandidateSort = "fit" | "asking" | "cashFlow" | "revenue";

/** Money fields that can be filtered, and whose blanks can be shown anyway. */
export type BuyerResearchMoneyField = "asking" | "cashFlow" | "revenue";

/**
 * Query for `GET runs/{runId}/candidates`. Every member is optional and an
 * absent one is the server's default; the client leaves defaults OFF the wire
 * so a page at its defaults issues the same request every time.
 */
export interface BuyerResearchCandidateListQuery {
  /** 50 or 75; absent = Any. Unscored rows are hidden whenever it is present. */
  minFit?: number;
  askMin?: number;
  askMax?: number;
  cfMin?: number;
  cfMax?: number;
  revMin?: number;
  revMax?: number;
  /** Comma list of {@link BuyerResearchMoneyField}. */
  showMissing?: string;
  /** Sent only as `true`; absent means dismissed rows are hidden. */
  dismissed?: true;
  /** Sent only as `true`. */
  latestOnly?: true;
  /** Leave out rows attached to the buyer (`isAttached`). Sent only as `true`. */
  hideAttached?: true;
  /** Quick search: reveal rows that couldn't be confirmed. Sent only as `true`. */
  showUnconfirmed?: true;
  /** Quick search: reveal rows that didn't match. Sent only as `true`. */
  showNoMatch?: true;
  q?: string;
  sort?: BuyerResearchCandidateSort;
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

/** Per money field counts for the hidden-row lines. */
export interface BuyerResearchMoneyFieldCountsDto {
  asking: number;
  cashFlow: number;
  revenue: number;
}

/**
 * The narrow row the list serves (R5). The full card — flags, reasons, notes,
 * contact — is {@link BuyerResearchCandidateDto}, fetched on expand.
 */
export interface BuyerResearchCandidateRowDto {
  id: string;
  title: string;
  city: string | null;
  state: string | null;
  industry: string | null;
  /**
   * NOT in the list contract today. Read when present so the industry column
   * can take the NAICS parenthetical back off (see `industryInWords`); absent,
   * the composed label renders whole — which is what it says.
   */
  naicsCode?: string | null;
  sourceKey: string;
  sourceDisplayName: string;
  marketplaceName: string | null;
  sourceUrl: string | null;
  linkOnly: boolean;
  clefListingId: string | null;
  askingPrice: number | null;
  cashFlow: number | null;
  cashFlowBasis: BuyerResearchCashFlowBasis | null;
  revenue: number | null;
  fitScore: number | null;
  recommended: boolean;
  isDismissed: boolean;
  attachedListingId: string | null;
  attachedAssociationId: string | null;
  existingListingId: string | null;
  existingListingDisplayName: string | null;
  possibleDuplicateOfListingId: string | null;
  possibleDuplicateDisplayName: string | null;
  /** The listing's own status from the source (e.g. "Available"). */
  status: string | null;
  batchNumber: number;
  /** A figure is a placeholder, or the figures contradict each other. Flags only (R10). */
  figuresImplausible: boolean;
  /** `placeholderAsking | placeholderCashFlow | placeholderRevenue | placeholderEbitda | inconsistent`. */
  implausibleReasons: string[];
  /** Quick search only. */
  matchStatus?: BuyerResearchMatchStatus | null;
  /** Quick search only: the first criterion that was not a yes, in words. */
  matchReason?: string | null;
  /**
   * The listing is attached to this buyer NOW — a live tie on the buyer's
   * Associated listings matches it. Read live on every run kind, so it clears
   * when the tie is removed. The ONE attached signal: `attachedListingId` is the
   * candidate's attach history and outlives a removal.
   */
  isAttached: boolean;
}

/** One page of the list, plus every count the filter bar needs. */
export interface BuyerResearchCandidatePageDto {
  items: BuyerResearchCandidateRowDto[];
  /** The page actually served, after the server's clamp. */
  page: number;
  pageSize: number;
  /** After the refinement lens, search, dismissed, latest, fit and money filters. */
  totalMatching: number;
  /** Every shown row on the run, before any of those. */
  totalOnRun: number;
  /** Live: shown rows the buyer has dismissed (R18). */
  dismissedOnRun: number;
  hiddenDismissed: number;
  /** Removed by the fit step, unscored rows included when a threshold is set. */
  belowFit: number;
  /** Hidden by a money filter because no figure was published. */
  hiddenForMissing: BuyerResearchMoneyFieldCountsDto;
  /** Hidden by a money filter because the figure looks wrong. */
  hiddenForImplausible: BuyerResearchMoneyFieldCountsDto;
  latestBatch: number;
  batchCount: number;
  /** Quick search: rows hidden because they couldn't be confirmed — the reveal's exact N. */
  hiddenUnconfirmed?: number;
  /** Quick search: rows hidden because they didn't match — the reveal's exact N. */
  hiddenNoMatch?: number;
  /** Quick search only. */
  quickCounts?: BuyerResearchQuickCountsDto | null;
}

export interface BuyerResearchRunSummaryDto {
  id: string;
  buyerLeadId: string;
  status: BuyerResearchRunStatus;
  terminalReason: string | null;
  requestedByUserId: string;
  requestedByName: string | null;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  sourcesAttempted: number;
  sourcesSucceeded: number;
  candidateCount: number;
  shownCount: number;
  alreadyTiedCount: number;
  dismissedCount: number;
  estimatedCostUsd: number;
  cancelRequested: boolean;
  error: string | null;

  /** Records Clef delivered on this run, INCLUDING rows the mapper dropped —
   * the number the daily allowance is spent in (D8). */
  clefRecordsUsed: number;

  /** What Clef's free count call said the filter matched, so the history can read
   * "showing the 40 newest of 312". Null when no Clef source ran; 0 would read as
   * "the filter matched nothing". */
  clefMatchCount: number | null;

  /** That count is for a WIDER filter than the run asked for, because Clef refused
   * a parameter and the call retried without it (RA3) — render "approximately N". */
  clefMatchCountApproximate: boolean;

  /** Whether another batch can be bought for this run, and why not when it
   * cannot (RC2). Null on a run whose state the server did not evaluate. */
  pull: BuyerResearchPullStateDto | null;

  /** Whether this run's query changes can be put back, and what already
   * happened to them (Area C). Null on the history LIST, where the server does
   * not evaluate it — no control there asks. */
  queryRevert: BuyerResearchQueryRevertStateDto | null;

  /** The ranked rows this run bought, scored and did not show, and whether they
   * can be asked for. Null on the history LIST, where the server does not count
   * them — a per-run query on a list read would spend on every reader to answer
   * a question only the run's own page asks. */
  withheld: BuyerResearchWithheldStateDto | null;

  /**
   * Ruling 7 — did Clef's financial bounds let listings that publish no figure
   * through, on this run's own query?
   *
   * 🔴 **NULL IS "NOBODY MEASURED", NEVER FALSE.** See
   * {@link BuyerResearchSourceLogEntryDto.nullsPassAnUpperBound}, the same
   * measurement on the source row; this is the column so the accumulated
   * population can be grouped rather than scanned out of a JSON blob.
   *
   * ⚠️ One probe on one query is EVIDENCE, not a ruling: it is measured with a
   * deliberate tolerance that leans toward "nulls pass". The value is in the
   * population it accumulates, which is why the copy states one measurement and
   * claims nothing further.
   */
  clefNullsPassAnUpperBound?: boolean | null;

  /**
   * RG23 — how many rows the batch being scored right now has to score, and
   * how many of its groups of twenty are done.
   *
   * 🔴 NULL MEANS NO SCORING PASS HAS STARTED on this batch — a first run still
   * searching, a pull still queued or buying — never "zero to score". A pull
   * clears both when it is queued, so the previous batch's finished count can
   * never read as progress on the next one. `scoringDone` counts every row a
   * finished group held, including rows in a group that failed, so it reaches
   * `scoringPlanned` when the last group ends; the rows that could not be scored
   * are named on the batch line, not here.
   */
  scoringPlanned?: number | null;
  scoringDone?: number | null;
  /** Absent on runs from before quick search; read as `Standard`. */
  kind?: BuyerResearchRunKind;
  quickSearch?: BuyerResearchQuickSearchDto | null;
}

/** One of `BuyerResearchRevertBlockers`. */
export type BuyerResearchRevertBlocker =
  | "nothing to revert"
  | "already reverted"
  | "run not finished";

/**
 * Whether this run's query changes can be put back, decided ENTIRELY by the
 * server — the same division of labour as the pull state: the client renders a
 * boolean and a sentence and computes nothing.
 *
 * 🔴 A revert applies to the **next** run and un-buys nothing. The records are
 * spent and the candidates stay. Every surface offering it must say so rather
 * than implying a refund.
 */
export interface BuyerResearchQueryRevertStateDto {
  canRevert: boolean;
  /** A CODE, null exactly when `canRevert` is true. */
  blocker: BuyerResearchRevertBlocker | null;
  revertedAtUtc: string | null;
  /** Resolved to a display name, or nothing — never a raw identity id. */
  revertedByName: string | null;
}

/**
 * Whether "Pull more" is available, decided ENTIRELY by the server (RC2). The
 * client renders a boolean and a sentence and computes NOTHING — no page
 * arithmetic, no budget subtraction, no guess at what a page costs.
 *
 * 🔴 `blocker` is a CODE, never a sentence, and it is null exactly when
 * `canPull` is true. The same vocabulary comes back as `details.code` on a
 * refused pull, so one copy map serves the disabled caption and the toast.
 */
/**
 * The scored-but-unshown remainder of a run.
 *
 * 🔴 **Since R1 a batch shows every row it scores, so a run scored today has
 * none.** Before R1 a batch ranked a fixed number of the records it bought
 * (`MaxCandidatesToRank`, 40) and showed fewer (`MaxShown`, 20), both since
 * retired; the rest carried a fit score, reasons and a summary and were shown to
 * nobody. The reveal stays for those runs. ⚠️ A refinement's promotion also
 * scores rows the run never showed, and those then count here too (the R21
 * ticket).
 *
 * 🔴 **`count` is PER RUN.** A run cut before R1 applied the cut to each batch,
 * so copy that says "the other twenty" is wrong on any run that was pulled —
 * compose from this number, never from a configured cap.
 */
export interface BuyerResearchWithheldStateDto {
  /** Ranked and not shown. An unscored row the batch also held back is
   * deliberately not counted: it has no score, no reasons and no summary, so
   * revealing it would put a blank card on screen. */
  count: number;

  /**
   * Whether the reveal endpoint would accept the request right now — false
   * while the run is busy and on a run that never completed.
   *
   * 🔴 **A FALSE HERE STILL DRAWS THE FOOTER.** The count is real and the rows
   * are real; what is missing is the ability to release them yet, so the client
   * states the count as prose and draws no button
   * (`researchCopy.results.withheldNotYet`). Reading this as "hide everything"
   * would tell a specialist a run held nothing back when it is holding back
   * fourteen.
   *
   * There is no blocker CODE beside it because there is exactly one reason —
   * the run has not settled — so a vocabulary would have one member and a
   * reader would learn nothing from it. That is a statement about the
   * cardinality of the reasons, not about whether the footer renders.
   */
  canReveal: boolean;
}

export interface BuyerResearchPullStateDto {
  canPull: boolean;
  blocker: BuyerResearchPullBlocker | null;

  /** The page a pull would buy, when there is one. */
  nextPage: number | null;

  /**
   * How many records the next pull would ask for — the CEILING, not a promise.
   * It is the whole buy: a pull takes several pages when the slot's page size is
   * smaller than a buy, so at 20 a page this reads 100. The source clamps its own
   * page size against the allowance Clef reports and stops early when Clef runs
   * out, so the honest copy is "costs up to N records".
   */
  recordsPerPull: number;

  /** What is left of this run's spend cap, in USD (RC1). Never negative. */
  remainingBudgetUsd: number;

  /**
   * RF7's second clause — how long an unchanged brief is refused for, in
   * hours, so the surface can name the window instead of gesturing at it.
   *
   * 🔴 **IT IS ON THE WIRE BECAUSE THE SENTENCE CANNOT INTERPOLATE A VALUE IT
   * DOES NOT HAVE.** The client's blocked-caption map is a static `Record` and
   * the window is CONFIGURATION (`ClefOptions.MinReRunHours`, 24 by default and
   * settable per slot). Hardcoding "24 hours" would be redundant on the default
   * and wrong the moment it moved, which is the reason
   * `ClefListingSource.ReRunRemedy` gives for declining to restate it.
   *
   * ⚠️ **NULL MEANS THE SERVER DID NOT SAY, and the surface then omits the
   * clause rather than guessing.** A window printed as "0 hours", or printed as
   * 24 on a slot configured otherwise, is worse than a sentence that does not
   * mention one.
   */
  reRunWindowHours?: number | null;

  /**
   * R2 — the part of this run's AI budget a pull may NOT spend, kept so Ask
   * Phoenix still works, in USD. Read by the `"ai budget reserved"` sentence; a
   * pull refused on it is refused on MODEL dollars, never on Clef records.
   *
   * ⚠️ Null means the server did not say, and the sentence then omits the
   * figure rather than guessing one.
   */
  reserveUsd?: number | null;
}

/**
 * Why a run cannot buy another batch — the `BuyerResearchPullBlockers` codes
 * verbatim.
 *
 * 🔴 The first three are STRUCTURAL and stored on the run; the rest are LIVE and
 * recomputed on every read. An allowance that is low right now, or a run that has
 * reached its cap, is a fact about the moment somebody presses the button, so
 * storing it would freeze an answer that changes at midnight UTC.
 *
 * `rank cap reached` is RESERVED — nothing produces it today. It is in the union
 * because the server's vocabulary contains it, and leaving it out would make the
 * copy map look exhaustive when it is not.
 *
 * 🔴 `clef did not run` and `clef page unknown` are the SAME server state told
 * apart: a null next page with no stored reason. The server picks between them on
 * whether the run carries a match count, which is the same fact this client uses
 * to decide whether to draw the control at all. They must never be collapsed back
 * into one — `clef did not run` rendered on a run whose own source log says
 * `clef · succeeded` is the defect this pair exists to close.
 */
export type BuyerResearchPullBlocker =
  | "no more pages"
  | "rank cap reached"
  | "empty page"
  | "clef did not run"
  | "clef page unknown"
  | "spend cap reached"
  | "allowance held back"
  | "run busy"
  | "run not completed"
  /**
   * R2 — the next pull would spend into the part of the AI budget kept for Ask
   * Phoenix. A MODEL-dollar refusal; `allowance held back` is the Clef-record
   * one, and the two never share a sentence.
   */
  | "ai budget reserved";

/**
 * Why Ask Phoenix will not take a refinement right now.
 *
 * 🔴 A vocabulary of its own, deliberately NOT {@link BuyerResearchPullBlocker}.
 * A pull and a refinement are refused for different reasons, and one list
 * serving both would carry members meaningless on either side.
 *
 * 🔴 `spend cap reached` and `allowance held back` are TWO BUDGETS and must
 * never be answered with one sentence: the first is this run's model cap, which
 * is configuration and can be raised; the second is Clef's shared daily record
 * allowance, which resets at 00:00 UTC. Telling somebody to wait for midnight
 * over the first is telling them to wait for something that will not help.
 */
export type BuyerResearchRefinementBlocker =
  | "run busy"
  | "run not completed"
  | "nothing to refine"
  | "spend cap reached"
  | "allowance held back"
  | "awaiting confirmation";

/** What one refinement turned out to be. */
export type BuyerResearchRefinementKind =
  | "in place"
  | "re-query"
  | "both"
  | "re-rank"
  | "refused";

/** Where one refinement is in its own short lifecycle. */
export type BuyerResearchRefinementStatus =
  | "classifying"
  | "awaiting confirmation"
  /**
   * The specialist confirmed a re-query and it is waiting for the worker to buy
   * it. The confirm POST returns at once with the refinement here and the run
   * `Refining`; the worker then moves it to `applying` and on to `applied` —
   * or `failed`, or back to `awaiting confirmation` carrying an `error`.
   */
  | "confirmed"
  | "applying"
  | "applied"
  | "refused"
  | "failed"
  | "cleared";

/**
 * What one refinement did to one candidate.
 *
 * 🔴 `promoted` is a candidate the RUN never showed — persisted below its rank
 * cut and never scored — that this refinement matched, SCORED, and then showed.
 * A promoted card always carries a score: a card with no score beside cards
 * that have one reads as a zero.
 */
export type BuyerResearchRefinementOutcome = "visible" | "hidden" | "promoted";

export interface BuyerResearchRefinementCandidateDto {
  candidateId: string;
  outcome: BuyerResearchRefinementOutcome;
  score: number | null;
  reason: string | null;
}

/** One refinement on one run: what was asked, what Phoenix made of it, and what it did. */
export interface BuyerResearchRefinementDto {
  id: string;
  runId: string;
  /** The specialist's words, verbatim and never rewritten. */
  text: string;
  kind: BuyerResearchRefinementKind;
  status: BuyerResearchRefinementStatus;
  requestedByUserId: string;
  requestedByName: string | null;
  createdAt: string;
  /** What Phoenix understood the instruction to mean, in one line. */
  rationale: string | null;
  /** Why Phoenix will not act on it. Non-null on a refusal. */
  refusalReason: string | null;
  visibleCount: number;
  /** Collapsed behind the restore line — never deleted. */
  hiddenCount: number;
  promotedCount: number;
  /** What Clef's FREE count said the proposed filter matches, before any spend. */
  matchCount: number | null;
  matchCountApproximate: boolean;
  recordsUsed: number;
  estimatedCostUsd: number;
  batchNumber: number | null;
  appliedAt: string | null;
  clearedAt: string | null;
  error: string | null;
  /**
   * The chain in force, oldest first, joined by " + " — "Plumbing in the title
   * + Texas". Null unless this refinement inherited something: a one-step chain
   * IS {@link text}, which the panel already renders, so a line repeating it
   * would say the same thing twice and imply composition where there is none.
   *
   * Built from the specialist's own words at every step. What Phoenix made of
   * them is {@link rationale}, shown separately.
   */
  summary: string | null;
}

/**
 * What it would cost to apply a classified refinement, shown BESIDE the button.
 *
 * Two budgets, two numbers: {@link recordsToSpend} comes off Clef's shared daily
 * allowance, {@link modelCostUsd} off this run's own cap.
 */
export interface BuyerResearchRefinementCostDto {
  recordsToSpend: number;
  modelCostUsd: number;
  /** True when applying it spends nothing at all — say so in those words. */
  isFree: boolean;
  remainingBudgetUsd: number;
}

export interface BuyerResearchRefinementStateDto {
  canRefine: boolean;
  blocker: BuyerResearchRefinementBlocker | null;
  /**
   * The refinement applied right now, or null. STICKY: one applied yesterday is
   * still here today, still showing the wording the specialist typed.
   */
  active: BuyerResearchRefinementDto | null;
  /** Newest first. A sibling of the run's transcript, not an extension of it. */
  history: BuyerResearchRefinementDto[];
}

/** The classifier's answer plus what acting on it would cost. */
export interface BuyerResearchRefinementResultDto {
  refinement: BuyerResearchRefinementDto;
  cost: BuyerResearchRefinementCostDto;
}

export interface BuyerResearchRunDto {
  run: BuyerResearchRunSummaryDto;
  brief: BuyerResearchBriefDto;
  sourceLog: BuyerResearchSourceLogEntryDto[];
  candidates: BuyerResearchCandidateDto[];
  /**
   * Ask Phoenix state, riding the SAME read as the candidates it is a lens
   * over. Two reads could be a second apart and disagree, and the surface would
   * then hide rows against a refinement that had already been cleared.
   *
   * Null only on a payload written before Ask Phoenix existed.
   */
  refinement: BuyerResearchRefinementStateDto | null;
}

export interface BuyerResearchAttachResultDto {
  candidateId: string;
  listingId: string;
  associationId: string;
  reused: boolean;
  listingDisplayName: string;
}

export interface DismissBuyerResearchCandidateRequest {
  reason: string | null;
}

export interface BuyerResearchDismissalDto {
  id: string;
  buyerLeadId: string;
  fingerprint: string;
  reason: string | null;
  dismissedByUserId: string;
  dismissedByName: string | null;
  dismissedAtUtc: string;
  titleSnapshot: string | null;
  sourceKeySnapshot: string | null;
  sourceUrlSnapshot: string | null;
}

const base = (buyerId: string) => `/buyers/${buyerId}/research`;

// ---------------------------------------------------------------------------
// Conversational search
//
// 🔴 EVERY SHAPE HERE CARRIES A BRIEF, NEVER A FILTER (RE3). A filter-shaped
// conversation cannot move the industry — the shipped Ask Phoenix classifier
// says so in as many words — so "just plumbing, not all trades" would degrade
// to a text match over rows already bought. The brief moves; the server
// re-resolves the NAICS codes and derives the filter exactly as a run does.
//
// 🔴 NOTHING IS PERSISTED SERVER-SIDE (RE2). The transcript and the working
// brief live here and are sent whole on every call. Resume rides a per-user
// preference row, which is capped — see `BuyerResearchConversationResume`.
// ---------------------------------------------------------------------------

/** Who said a line. `note` is written by this client, never by a model. */
export type BuyerResearchConversationRole = "specialist" | "phoenix" | "note";

/** What one turn did. Derived server-side from what survived the mapping. */
export type BuyerResearchConversationKind =
  | "narrowed"
  | "widened"
  | "unchanged"
  | "routed"
  | "asked"
  | "refused";

/**
 * One measured query and what Clef said it matches.
 *
 * 🔴 `matchCount` is the number the buy will honour — it comes from the free
 * count against the filter this exact brief derives, which is the same
 * derivation a run performs. `approximate` is true when Clef refused a
 * parameter and answered a WIDER question; say so rather than showing it flat.
 */
export interface BuyerResearchConversationCount {
  label: string;
  matchCount: number;
  approximate: boolean;
  isCurrent: boolean;
  brief: BuyerResearchBriefDto | null;
}

export interface BuyerResearchConversationEntry {
  role: BuyerResearchConversationRole;
  text: string;
  atUtc: string;
  counts: BuyerResearchConversationCount[] | null;
  kind: BuyerResearchConversationKind | null;
}

/**
 * The two guards that can refuse a run, read while the specialist is still
 * building rather than at RUN.
 */
export interface BuyerResearchConversationGuards {
  runsUsedInWindow: number;
  runsAllowedInWindow: number;
  nextRunAllowedAtUtc: string | null;
  reRunGuardWouldRefuse: boolean;
  identicalRunAtUtc: string | null;
  reRunWindowHours: number;
}

/**
 * Ruling 1's route: a clause Clef cannot be asked to filter on, and the field
 * an in-place filter reads to answer it once the records are here.
 */
export interface BuyerResearchConversationRoute {
  clause: string;
  field: string;
  sentence: string;
}

export interface BuyerResearchConversationTurn {
  reply: string;
  kind: BuyerResearchConversationKind;
  /** The brief as this turn would leave it, or null when nothing moved. */
  proposedBrief: BuyerResearchBriefDto | null;
  counts: BuyerResearchConversationCount[];
  /** Queries the turn described and did not count. An offer, not a refusal. */
  unmeasured: string[];
  guards: BuyerResearchConversationGuards;
  route: BuyerResearchConversationRoute | null;
  refusalReason: string | null;
}

export interface BuyerResearchConversationCountResult {
  count: BuyerResearchConversationCount;
  guards: BuyerResearchConversationGuards;
}

/**
 * A resumed conversation.
 *
 * ⚠️ `truncated` exists so the copy can SAY so. The resume store is a per-user
 * preference row with an 8 KB ceiling; a long conversation comes back with its
 * oldest turns dropped, and a specialist must be told rather than left to
 * notice.
 */
export interface BuyerResearchConversationResume {
  /**
   * The working brief as it stood when the conversation was parked, or null
   * when there was nothing to resume.
   *
   * 🔴 **A resumed COUNT carries no brief of its own, deliberately** — see
   * {@link BuyerResearchConversationCount.brief}. This one is the conversation's
   * whole state and IS carried, which is why the truncation copy can promise
   * the search came back in full even when the history did not.
   */
  brief: BuyerResearchBriefDto | null;
  transcript: BuyerResearchConversationEntry[];
  truncated: boolean;
  droppedTurns: number;
  /**
   * When the parked blob was written, or null when there was nothing to resume.
   * Copy, never a control: a blob older than the 30-day TTL is not returned at
   * all, so this never has to be compared against anything.
   */
  savedAtUtc?: string | null;
}

/**
 * What the drawer needs the moment it opens: the brief, the two guards that can
 * refuse a run, and whatever conversation is waiting to be resumed.
 *
 * 🔴 **A WRAPPER RATHER THAN THREE MEMBERS ON {@link BuyerResearchBriefDto},
 * because that record is ALSO the start-run REQUEST body.** A `guards` member
 * on it would be meaningful outbound and ignored inbound — a field whose
 * truth depends on which direction it is travelling, which is the shape a
 * client eventually sends back populated and nobody notices.
 *
 * 🔴 **`guards` describe `brief` and NOT `resume.brief`.** The daily-run half
 * is per BUYER and identical either way; the re-run half is per QUERY, so a
 * client that adopts the resumed brief is reading a re-run guard about the
 * brief it just replaced — for as long as it takes to count, which is the
 * first thing the drawer does, and every count and every turn returns guards of
 * its own.
 */
export interface BuyerResearchBriefReadDto {
  brief: BuyerResearchBriefDto;
  guards: BuyerResearchConversationGuards;
  resume: BuyerResearchConversationResume;
}

/** The per-turn free-count budget. A budget the turn spends, never a refusal. */
export const BUYER_RESEARCH_MAX_COUNTS_PER_TURN = 3;

/**
 * The per-user preference key one buyer's in-flight conversation resumes from.
 *
 * 🔴 **The shape is `^[a-z0-9-]{1,64}$` — NO SLASH — and a key that breaks
 * it fails TWICE, both times in silence.** `MePreferencesController` binds
 * `{key}` as ONE path segment, so a `/` matches no route and 404s before the
 * service is reached; `StaffUserPreferenceService.RequireKey` then rejects the
 * charset separately. Neither failure surfaces to a specialist — the
 * conversation simply never comes back.
 *
 * 🔴 **The prefix is deliberately not `buyer-research-conversation-`:**
 * that plus a 36-character GUID is 64 characters EXACTLY — legal, with zero
 * headroom, so the next rename to a longer word would start 400ing at runtime
 * with nothing in the type system to catch it. This one is 58.
 *
 * Pinned against the server's own
 * `BuyerResearchConversationContract.PreferenceKeyFor` by
 * `BuyerResearchConversationWiringTests`, which reads THIS FILE from disk —
 * the shared-fixture idiom, because pinning the C# constant against itself
 * would pass while the two tiers wrote to different rows.
 */
export function conversationPreferenceKey(buyerId: string): string {
  return `research-conversation-${buyerId}`;
}

export const buyerResearchApi = {
  /**
   * Everything the research drawer needs to open: the AI-proposed brief, the
   * two guards that can refuse a run, and any conversation waiting to be
   * resumed.
   *
   * Costs an AI call. 🔴 Persists nothing — the resume ROW is written at the
   * end of each conversational turn, not here.
   */
  buildBrief: (buyerId: string) =>
    api.post<BuyerResearchBriefReadDto>(`${base(buyerId)}/brief`),
  /** Queue a run with the brief exactly as the specialist edited it. */
  startRun: (buyerId: string, brief: BuyerResearchBriefDto) =>
    api.post<BuyerResearchRunDto>(`${base(buyerId)}/runs`, {
      brief,
    } satisfies StartBuyerResearchRunRequest),
  /**
   * Quick search: the server reads the typed sentence SYNCHRONOUSLY and queues a
   * run for it — no brief, no confirmation step. An unreadable sentence is a 400
   * whose message is the plain sentence to show, and no run is created.
   */
  startQuickSearch: (buyerId: string, text: string) =>
    api.post<BuyerResearchRunSummaryDto>(`${base(buyerId)}/quick-runs`, {
      text,
    } satisfies StartBuyerResearchQuickSearchRequest),
  /** Newest first. */
  listRuns: (buyerId: string) => api.get<BuyerResearchRunSummaryDto[]>(`${base(buyerId)}/runs`),
  /**
   * The run. `includeCandidates: false` asks for `candidates: []` — the POLLING
   * hook's read, which no longer carries every row every five seconds. The
   * list itself is {@link listCandidates}; every other endpoint that returns a
   * run keeps the full shape.
   */
  getRun: (buyerId: string, runId: string, options?: { includeCandidates?: boolean }) =>
    options?.includeCandidates === false
      ? api.get<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}`, {
          includeCandidates: false,
        })
      : api.get<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}`),
  /**
   * One page of the run's candidates, filtered, sorted and paged by the SERVER
   * (R5). The client never sorts: there is one ordering, in the service.
   */
  listCandidates: (buyerId: string, runId: string, query: BuyerResearchCandidateListQuery) =>
    api.get<BuyerResearchCandidatePageDto>(
      `${base(buyerId)}/runs/${runId}/candidates`,
      query,
    ),
  /** The full candidate, fetched when its row is expanded. 404 when it is not on this run. */
  getCandidate: (buyerId: string, runId: string, candidateId: string) =>
    api.get<BuyerResearchCandidateDto>(
      `${base(buyerId)}/runs/${runId}/candidates/${candidateId}`,
    ),
  cancelRun: (buyerId: string, runId: string) =>
    api.post<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}/cancel`),
  /**
   * "Pull more" — buy the run's NEXT page, rank it and APPEND it. Returns the
   * whole run, so the caller replaces its copy rather than merging a delta.
   *
   * 🔴 QUEUED, not synchronous: this resolves as soon as the pull is accepted,
   * with the run in `PullQueued`. The worker buys and ranks it; the polling read
   * carries the run through `Pulling` and back to a terminal status, and a
   * failure lands in the source log rather than as an error from this call.
   * A REFUSAL is still a 400 carrying the blocker CODE in `details.code` — see
   * {@link pullRefusalBlocker}.
   */
  pullMore: (buyerId: string, runId: string) =>
    api.post<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}/pull`),
  /**
   * Show the ranked rows this run bought, scored and held back.
   *
   * 🔴 **It buys nothing.** The records are spent and the model has already
   * scored these candidates — this is the one control on the surface that puts
   * more listings on screen for no money. What it costs is LATER: a refinement
   * re-scores what is shown, so a revealed run's every future re-rank is larger.
   *
   * Idempotent. No card crosses a batch — `batchNumber` is the read order's
   * first key and a reveal does not write it — but 🔴 **a revealed row CAN sort
   * above one already on screen, and the claim that it merely appends is
   * false.** The shown cut is fit score alone; the read order puts
   * `recommended` ABOVE fit score. So a revealed recommended row outranks an
   * already-shown unrecommended one, which is the ordinary case rather than an
   * exotic one, because the recommendation gate is what strips `recommended`
   * from a high scorer while a lower scorer keeps it.
   */
  revealWithheld: (buyerId: string, runId: string) =>
    api.post<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}/reveal-withheld`),
  /**
   * Area C — reject what the probe did to this run's query.
   *
   * 🔴 It applies to the buyer's **next** run and un-buys nothing: the records
   * are spent and the candidates stay. Any surface calling this must say so on
   * the way in rather than implying a refund.
   *
   * A refusal is a 400 carrying the blocker CODE in `details.code` — see
   * {@link revertRefusalBlocker}.
   */
  revertQueryChanges: (buyerId: string, runId: string) =>
    api.post<BuyerResearchRunDto>(`${base(buyerId)}/runs/${runId}/revert-query-changes`),
  /** Idempotent: a second attach returns the same ids with `reused` = true. */
  attach: (buyerId: string, runId: string, candidateId: string) =>
    api.post<BuyerResearchAttachResultDto>(
      `${base(buyerId)}/runs/${runId}/candidates/${candidateId}/attach`,
    ),
  /**
   * D7 — buy the listing broker's contact for ONE candidate and update it in
   * place. Costs a record, and is idempotent on the candidate's
   * `contactFetchedAtUtc`, so a second press makes no second request. 400s with a
   * reason when Clef cannot produce contact.
   */
  fetchBrokerContact: (buyerId: string, runId: string, candidateId: string) =>
    api.post<BuyerResearchCandidateDto>(
      `${base(buyerId)}/runs/${runId}/candidates/${candidateId}/broker-contact`,
    ),
  dismiss: (buyerId: string, runId: string, candidateId: string, reason?: string | null) =>
    api.post<BuyerResearchDismissalDto>(
      `${base(buyerId)}/runs/${runId}/candidates/${candidateId}/dismiss`,
      { reason: reason?.trim() || null } satisfies DismissBuyerResearchCandidateRequest,
    ),
  /**
   * Ask Phoenix — classify a refinement a specialist typed. QUEUED: this
   * resolves once the refinement row exists, and the classifier answers on the
   * worker. Poll the run for the result.
   *
   * 🔴 It spends NOTHING on its own. A re-query stops at `awaiting
   * confirmation` holding a MEASURED match count, and records are bought only
   * by {@link confirmRefinement}.
   *
   * A refusal to take the refinement at all is a 400 carrying the blocker CODE
   * in `details.code` — see {@link refineRefusalBlocker}.
   */
  refine: (buyerId: string, runId: string, text: string) =>
    api.post<BuyerResearchRefinementResultDto>(`${base(buyerId)}/runs/${runId}/refine`, {
      text,
    } satisfies BuyerResearchRefineRequest),
  /**
   * Approve the spend a classified re-query is waiting on. This is the click
   * decision 1 exists for: the count was free, the number is on screen, and
   * nothing has been bought until now.
   *
   * 🔴 QUEUED: this resolves at once with the refinement `confirmed` and the run
   * `Refining`. The worker does the buy; the polling read carries the run back
   * to a terminal status, and a failure lands on the refinement's `error`
   * rather than as an error from this call.
   */
  confirmRefinement: (buyerId: string, runId: string, refinementId: string) =>
    api.post<BuyerResearchRunDto>(
      `${base(buyerId)}/runs/${runId}/refinements/${refinementId}/confirm`,
    ),
  /**
   * Clear the applied refinement. Restores every hidden candidate and puts the
   * run's ORIGINAL search back as the pull target.
   *
   * 🔴 It deletes nothing: the refinement stays in the history with what it
   * did, and any batch it bought stays bought.
   */
  clearRefinement: (buyerId: string, runId: string, refinementId: string) =>
    api.post<BuyerResearchRunDto>(
      `${base(buyerId)}/runs/${runId}/refinements/${refinementId}/clear`,
    ),
  listDismissals: (buyerId: string) =>
    api.get<BuyerResearchDismissalDto[]>(`${base(buyerId)}/dismissals`),
  /** Undismiss = soft-delete of the dismissal row (RL14). */
  undismiss: (buyerId: string, dismissalId: string) =>
    api.delete<void>(`${base(buyerId)}/dismissals/${dismissalId}`),

  /**
   * Count the brief as it now stands. FREE at the vendor, and the number a
   * later RUN will honour.
   *
   * 🔴 Not rate-limited, deliberately: the staff AI bucket is about model
   * spend and this makes no model call. Its ceiling is the vendor's own
   * request rate, which the Clef client bounds.
   */
  countBrief: (buyerId: string, brief: BuyerResearchBriefDto) =>
    api.post<BuyerResearchConversationCountResult>(`${base(buyerId)}/conversation/count`, {
      brief,
    }),

  /**
   * One conversational turn against the live brief. Makes one model call and
   * up to three free counts; persists nothing.
   */
  converse: (
    buyerId: string,
    brief: BuyerResearchBriefDto,
    transcript: BuyerResearchConversationEntry[],
    instruction: string,
  ) =>
    api.post<BuyerResearchConversationTurn>(`${base(buyerId)}/conversation/turn`, {
      brief,
      transcript,
      instruction,
    }),
};

/** `BuyerResearchErrorCodes.DailyLimit` — the per-buyer rolling-24h run limit (D5). */
export const BUYER_RESEARCH_DAILY_LIMIT_CODE = "buyerResearchDailyLimit";

/**
 * The refusal that means "come back later", told apart from every other 400
 * by its CODE. `ApiError.details` is the only structured channel that survives
 * a 400; the message beside it is copy and will be reworded, so matching on it
 * would stop matching silently.
 */
export function isDailyLimitRefusal(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  const details = e.details as { code?: unknown } | null | undefined;
  return details?.code === BUYER_RESEARCH_DAILY_LIMIT_CODE;
}

/**
 * The BLOCKER a refused pull carries, or null when the error is not one.
 *
 * 🔴 Read from `details.code` and never from the message, for the reason its
 * daily-limit sibling states: the message is copy and will be reworded, and a
 * prose match stops matching silently. The value is the same
 * {@link BuyerResearchPullBlocker} the control renders when it is drawn disabled,
 * so a caller has ONE copy map for both.
 *
 * A code the client does not recognise comes back as-is rather than null: the
 * caller can then fall back to the server's own message, which is plain words
 * about what happened. Returning null there would make a refusal it could not
 * name indistinguishable from a refusal that never happened.
 */
export function pullRefusalBlocker(e: unknown): string | null {
  return refusalCode(e);
}

/**
 * The blocker code a REVERT refusal carries, or null when it is not one.
 *
 * Same mechanism as {@link pullRefusalBlocker} and a separate name on purpose:
 * the two carry different vocabularies, and one name across both would invite a
 * caller to answer a revert refusal out of the pull copy map. The extraction is
 * shared so they cannot drift.
 */
export function revertRefusalBlocker(e: unknown): string | null {
  return refusalCode(e);
}

/**
 * The blocker code an ASK PHOENIX refusal carries, or null when it is not one.
 *
 * A third name over the same extraction, for the same reason the second one
 * exists: the three vocabularies are different, and one name across all of them
 * would invite a caller to answer a refinement refusal out of the pull copy map.
 */
export function refineRefusalBlocker(e: unknown): string | null {
  return refusalCode(e);
}

/**
 * 🔴 Read from `details.code`, never from the message: the message is copy and
 * will be reworded, and a prose match stops matching silently.
 *
 * A code the client does not recognise comes back AS-IS rather than null, so a
 * caller can fall back to the server's own words. Returning null there would
 * make a refusal it could not name indistinguishable from one that never
 * happened. The daily-limit code is excluded because it is its own refusal with
 * its own surface.
 */
function refusalCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const details = e.details as { code?: unknown } | null | undefined;
  return typeof details?.code === "string" && details.code !== BUYER_RESEARCH_DAILY_LIMIT_CODE
    ? details.code
    : null;
}

/** The `nextAllowedAtUtc` a daily-limit refusal carries, or null when it does not. */
export function dailyLimitNextAllowedAt(e: unknown): string | null {
  if (!isDailyLimitRefusal(e)) return null;
  const details = (e as ApiError).details as { nextAllowedAtUtc?: unknown } | null | undefined;
  return typeof details?.nextAllowedAtUtc === "string" ? details.nextAllowedAtUtc : null;
}
